import { useAutoneTranslation } from '@autone/translations';
import { isFetchBaseQueryError } from '@autone/utils';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { type Control, useWatch } from 'react-hook-form';
import * as z from 'zod';

import { type APP_NAME } from '../consts';
import { type DownloadAssortmentTemplateFormSchema } from '../features/assortment-template-modals/download-template-modal/DownloadTemplateModal.schema';
import {
  useValidateAssortmentTemplateExportLimitQuery,
  type ValidateAssortmentTemplateExportLimitError,
} from '../redux/assortment';

import {
  useWatchLocationClustersScope,
  useWatchLocationScope,
  useWatchProductScope,
} from './form-hooks';

const validateAssortmentTemplateExportLimit400Error: z.ZodType<ValidateAssortmentTemplateExportLimitError> =
  z.object({
    error_code: z.literal('EXPORT_LIMIT_OUT_OF_BOUNDS'),
    detail: z.string(),
    body: z.object({
      lower_limit: z.number(),
      upper_limit: z.number(),
      requested_combinations: z.number(),
    }),
  });

const is400Error = (
  error: unknown,
): error is ValidateAssortmentTemplateExportLimitError => {
  return validateAssortmentTemplateExportLimit400Error.safeParse(error).success;
};

const isLimitOutOfBoundsError = (
  error: ValidateAssortmentTemplateExportLimitError,
): error is Extract<
  ValidateAssortmentTemplateExportLimitError,
  { error_code: 'EXPORT_LIMIT_OUT_OF_BOUNDS' }
> => {
  return error.error_code === 'EXPORT_LIMIT_OUT_OF_BOUNDS';
};

type RowCountData = {
  exportRowCount: number | null;
  exportRowCountLowerLimit: number | null;
  exportRowCountUpperLimit: number | null;
};
type UseGetAssortmentTemplateExportRowCountReturn = {
  isLoading: boolean;
} & RowCountData;

export const useGetAssortmentTemplateExportRowCount = (
  control: Control<DownloadAssortmentTemplateFormSchema>,
): UseGetAssortmentTemplateExportRowCountReturn => {
  const { common } = useAutoneTranslation<typeof APP_NAME>();
  const [rowCountData, setRowCountData] = useState<RowCountData>({
    exportRowCount: null,
    exportRowCountLowerLimit: null,
    exportRowCountUpperLimit: null,
  });

  const {
    departments,
    subDepartments,
    collectionTypes,
    seasons,
    events,
    brands,
    productIds,
    manufacturers,
    classes,
    subClasses,
    productGroups,
  } = useWatchProductScope(control) || {};
  const { locationTypes, regions, countries, locations } =
    useWatchLocationScope(control) || {};
  const locationClusters = useWatchLocationClustersScope(control);
  const aggregationType = useWatch({ control, name: 'aggregationType' });

  const { data, error, isLoading, isFetching } =
    useValidateAssortmentTemplateExportLimitQuery(
      {
        body: {
          aggregation_type: aggregationType,
          filters: {
            product: {
              product_groups: productGroups,
              departments: departments,
              sub_departments: subDepartments,
              collection_types: collectionTypes,
              seasons: seasons,
              events: events,
              brands: brands,
              manufacturers: manufacturers,
              classes: classes,
              sub_classes: subClasses,
              product_ids: productIds,
            },
            geographic: {
              location_types: locationTypes,
              regions: regions,
              countries: countries,
              locations: locations,
            },
            location_cluster: {
              location_clusters: locationClusters,
            },
          },
        },
        extraArgs: {
          hideSnackbar: true,
        },
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  useEffect(() => {
    if (error) {
      if (isFetchBaseQueryError(error) && is400Error(error.data)) return;
      enqueueSnackbar({
        message: common('error-text'),
        variant: 'error',
      });
    }
  }, [error, common]);

  useEffect(() => {
    const outOfBoundsErrorData =
      isFetchBaseQueryError(error) &&
      is400Error(error.data) &&
      isLimitOutOfBoundsError(error.data)
        ? error.data
        : null;

    if (!isLoading && !isFetching) {
      if (!error) {
        setRowCountData({
          exportRowCount: data?.combinations ?? null,
          exportRowCountLowerLimit: null,
          exportRowCountUpperLimit: null,
        });
      } else if (outOfBoundsErrorData) {
        setRowCountData({
          exportRowCount:
            outOfBoundsErrorData?.body.requested_combinations ?? null,
          exportRowCountLowerLimit:
            outOfBoundsErrorData?.body.lower_limit ?? null,
          exportRowCountUpperLimit:
            outOfBoundsErrorData?.body.upper_limit ?? null,
        });
      } else {
        setRowCountData({
          exportRowCount: null,
          exportRowCountLowerLimit: null,
          exportRowCountUpperLimit: null,
        });
      }
    }
  }, [error, isFetching, isLoading, data?.combinations]);

  return {
    ...rowCountData,
    isLoading: isLoading,
  };
};
